import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { AdminUsersService } from '../../../openapi'
import { commandApi } from '../../../services/birdbrain-api-client'
import { serverApiFetch } from '../../../services/server-api-client'
import { apiCall } from '../../core'
import { errorMessage } from '@/lib/utils'

// The users LIST now lives in react-query (hooks/use-admin-users.ts) — this
// slice only carries the user-detail page state (current user + the audited
// account mutations).

// Types for our user data
interface User {
  id: string
  email: string
  name: string
  role: string
  status: string
  joinDate: string
  lastActive: string
  devices: string[]
  createdAt: string
  updatedAt: string
  onboardingVersionDone: number
  onboardingData: {
    completedVersion: number
    [key: string]: unknown
  }
  timezone?: string
  avatarId?: string | null
  avatar?: {
    id: string
    publicUrl: string
    mimeType: string
    size: number
    [key: string]: unknown
  } | null
  preferredAgentCategories?: string[] | null
  /** Set when an admin froze the account (login + autopilot blocked). Null = active. */
  suspendedAt?: string | null
  suspendedReason?: string | null
  /** Persisted plan/billing snapshot (single-user endpoint enrichment; optional for wire-compat). */
  plan?: import('../../../hooks/use-admin-users').AdminUserPlan
  /** Businesses the user has an active membership in (oldest first). */
  businesses?: import('../../../hooks/use-admin-users').AdminUserBusinessRef[]
}

interface AllUsersState {
  error: string | null
  currentUser: User | null
  currentUserLoading: boolean
  resettingOnboarding: boolean
  deletingUser: boolean
}

const initialState: AllUsersState = {
  error: null,
  currentUser: null,
  currentUserLoading: false,
  resettingOnboarding: false,
  deletingUser: false,
}

interface UserApiResponse {
  success: boolean
  user?: User
  message?: string
  error?: string
}

/** Arg for fetchUserById. `forceRefresh` skips any cached copy — used after
 *  admin commands (suspend/unsuspend/...) so the fresh server state lands in Redux. */
export type FetchUserByIdArg = string | { userId: string; forceRefresh?: boolean }

export const fetchUserById = createAsyncThunk(
  'allUsers/fetchUserById',
  async (arg: FetchUserByIdArg, { rejectWithValue }) => {
    const userId = typeof arg === 'string' ? arg : arg.userId

    try {
      const response = await serverApiFetch<UserApiResponse>(`/v1/admin/users/${userId}`)

      if (!response.success || !response.user) {
        return rejectWithValue({ message: response.message || 'User not found' })
      }

      return response.user
    } catch (error) {
      return rejectWithValue({ message: errorMessage(error) || 'User not found' })
    }
  }
)

/** buildApiError prefixes messages with "API request failed (409): " — strip it
 *  so dialogs and inline alerts show the server's own words, as before. */
const stripApiErrorPrefix = (message?: string): string =>
  (message ?? '').replace(/^API request failed \(\d+\):\s*/, '')

// Audited path: role changes go through the birdbrain-api command proxy
// (POST /command/users/:id/set-role, action 'user.set_role') so they land in
// the Command Log. The fulfilled payload stays the server's verbatim response
// ({ success, message, user }) — the proxy forwards it under `response`.
export const setUserRole = createAsyncThunk(
  'allUsers/setUserRole',
  async (
    params: {
      userId: string
      email: string
      role: 'admin' | 'developer' | 'moderator' | 'system' | 'user'
    },
    { rejectWithValue }
  ) => {
    try {
      const result = await commandApi.setUserRole(params.userId, {
        role: params.role,
        targetLabel: params.email,
      })
      return result.response as UserApiResponse
    } catch (error) {
      return rejectWithValue({
        message: stripApiErrorPrefix(errorMessage(error)) || 'Failed to update user role',
      })
    }
  }
)

// TODO(audit): promoteUser and resetUserOnboarding below still hit the main
// server directly (unaudited) — route them through the command proxy too.

export const promoteUser = createAsyncThunk(
  'allUsers/promoteUser',
  async (params: { email: string }, { rejectWithValue }) => {
    return await apiCall(AdminUsersService.adminUsersControllerPromoteUserV1, rejectWithValue, {
      email: params.email,
    })
  }
)

export const resetUserOnboarding = createAsyncThunk(
  'allUsers/resetUserOnboarding',
  async (userId: string, { rejectWithValue }) => {
    const result = await apiCall(
      AdminUsersService.adminUsersControllerResetUserOnboardingV1,
      rejectWithValue,
      userId
    )

    if (!result.success || !result.user) {
      throw new Error(result.message || 'Failed to reset onboarding')
    }

    return result.user
  }
)

// Hard delete — audited path through the birdbrain-api command proxy
// (POST /command/users/:id/delete, action 'user.delete'), which forwards to
// the server's admin DELETE endpoint and writes a Command Log row on success
// and failure alike. On success the user is purged across DB + RC + GCS; we
// evict it from local state so the next list fetch reflects reality. On 409
// (multi-member-business owner) the server's verbatim message is surfaced via
// rejectWithValue so the UI can show it in-context.
export const deleteUser = createAsyncThunk<
  string,
  string,
  { rejectValue: { status?: number; message: string } }
>('allUsers/deleteUser', async (userId, { rejectWithValue, getState }) => {
  try {
    // Snapshot the target's email for the audit row (label only; the proxy
    // keys the server call by the path id).
    const state = getState() as { allUsers: AllUsersState }
    const target = state.allUsers.currentUser?.id === userId ? state.allUsers.currentUser : null
    await commandApi.deleteUser(userId, { targetLabel: target?.email })
    return userId
  } catch (err) {
    // buildApiError stamps `status`; the openapi ApiError carries `body.message`.
    const apiError = err as { status?: number; body?: { message?: string }; message?: string }
    const status = apiError?.status
    const message =
      stripApiErrorPrefix(apiError?.body?.message || apiError?.message) || 'Failed to delete user'
    return rejectWithValue({ status, message })
  }
})

// Slice
const usersSlice = createSlice({
  name: 'allUsers',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    clearCurrentUser: state => {
      state.currentUser = null
      state.currentUserLoading = false
    },
    updateCurrentUserRole: (state, action) => {
      if (state.currentUser) {
        state.currentUser.role = action.payload
      }
    },
  },
  extraReducers: builder => {
    // Set user role
    builder
      .addCase(setUserRole.fulfilled, () => {
        // Optionally refresh users list after role change
      })
      .addCase(setUserRole.rejected, (state, action) => {
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to update user role'
      })

    // Promote user
    builder
      .addCase(promoteUser.fulfilled, () => {
        // Optionally refresh users list after promotion
      })
      .addCase(promoteUser.rejected, (state, action) => {
        state.error =
          (action.payload as { message?: string } | undefined)?.message || 'Failed to promote user'
      })

    // Fetch user by ID
    builder
      .addCase(fetchUserById.pending, (state, action) => {
        const arg = action.meta.arg
        const userId = typeof arg === 'string' ? arg : arg.userId
        // Forced refreshes of the already-loaded user run silently in the
        // background so the detail page doesn't flash its loading state.
        const isSilentRefresh =
          typeof arg !== 'string' && !!arg.forceRefresh && state.currentUser?.id === userId
        if (!isSilentRefresh) {
          state.currentUserLoading = true
        }
        state.error = null
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.currentUserLoading = false
        state.currentUser = action.payload
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.currentUserLoading = false
        state.currentUser = null
        state.error =
          (action.payload as { message?: string } | undefined)?.message || 'Failed to fetch user'
      })

    // Reset user onboarding
    builder
      .addCase(resetUserOnboarding.pending, state => {
        state.resettingOnboarding = true
        state.error = null
      })
      .addCase(resetUserOnboarding.fulfilled, (state, action) => {
        state.resettingOnboarding = false
        // Update current user if it matches the reset user
        if (state.currentUser && state.currentUser.id === action.payload.id) {
          state.currentUser = action.payload
        }
      })
      .addCase(resetUserOnboarding.rejected, (state, action) => {
        state.resettingOnboarding = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to reset onboarding'
      })

    // Delete user (admin-initiated hard delete)
    builder
      .addCase(deleteUser.pending, state => {
        state.deletingUser = true
        state.error = null
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deletingUser = false
        if (state.currentUser?.id === action.payload) {
          state.currentUser = null
        }
      })
      .addCase(deleteUser.rejected, state => {
        state.deletingUser = false
        // Intentionally not setting state.error here: delete errors (e.g. the 409
        // "Cancel your active subscription") are surfaced in-context via the
        // thunk's rejectWithValue payload so the dialog can show them as an
        // inline alert. Writing to the shared error would trip UserDetailPage's
        // full-page "Error loading user" UI, which reads selectUsersError.
      })
  },
})

export const { clearError, clearCurrentUser, updateCurrentUserRole } = usersSlice.actions
export const usersReducer = usersSlice.reducer

// Base selectors
const selectAllUsersState = (state: { allUsers: AllUsersState }) => state.allUsers

// Memoized selectors
export const selectUsersError = createSelector(
  [selectAllUsersState],
  allUsersState => allUsersState.error
)

export const selectCurrentUser = createSelector(
  [selectAllUsersState],
  allUsersState => allUsersState.currentUser
)

export const selectCurrentUserLoading = createSelector(
  [selectAllUsersState],
  allUsersState => allUsersState.currentUserLoading
)

export const selectResettingOnboarding = createSelector(
  [selectAllUsersState],
  allUsersState => allUsersState.resettingOnboarding
)

export const selectDeletingUser = createSelector(
  [selectAllUsersState],
  allUsersState => allUsersState.deletingUser
)
